
import type { BarChartConfig, BarChartData, BarChartDataItem, Params } from '@empathyco/insights'
import type { ComputedRef, PropType, SetupContext } from 'vue'

import type { FeatureIdEnum } from '../../../enums/feature-id.enum'
import type { ToolReportEntry } from '../../../models/domain/tool-report-entry'

import { BarChartComponent } from '@empathyco/insights'
import { computed, defineComponent, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue'
import {
  useFilters,
  useToolingReportCtrPerformance,
  useToolingReportList,
  useToolingReportStats,
} from '../../../composables'
import ToolReportingDataStateHandler, { DataStateEnum } from './data-state-handler.vue'

export default defineComponent({
  name: 'ToolReportingBarChart',
  components: {
    ToolReportingDataStateHandler,
  },
  props: {
    tool: {
      type: String as PropType<FeatureIdEnum>,
      required: true,
    },
  },
  setup(props, { emit }: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const { stats } = useToolingReportStats(props.tool)
    const { fetchData, loading } = useToolingReportList(props.tool)
    const { selectedDates } = useFilters()
    const { getBarColor } = useToolingReportCtrPerformance()
    const barChart = ref<HTMLElement>(null)
    const barChartData = ref<ToolReportEntry[] | undefined>(undefined)

    const isDateSelected = computed(() => selectedDates.value[0])
    const hasBarChartData = computed(() => barChartData.value?.length)

    const params: ComputedRef<Params<BarChartConfig, BarChartData>> = computed(() => ({
      config: {
        locale: 'en',
        transitionsDurationInMillis: 10,
        yAxisLabelFormatFn: (domainValue: BarChartDataItem['value']): string => {
          return `${$epb.utils.formatNumber(domainValue)}%`
        },
        handleItemClickFn: (item: BarChartDataItem): void => {
          const toolReportEntry = barChartData.value?.find(
            (entry: ToolReportEntry) => entry.id === item.id,
          )
          emit('itemClick', toolReportEntry)
        },
        getBarColorFn: getBarColor,
      },

      data: {
        avg: stats.value?.clickCountPct,
        items: barChartData.value?.map(({ name, ctr, id }: ToolReportEntry): BarChartDataItem => {
          return {
            name,
            value: ctr,
            id,
          }
        }),
      },
    }))

    const dataState = computed((): DataStateEnum => {
      if (loading.value || !stats.value) {
        return DataStateEnum.LOADING
      }
      if (hasBarChartData.value && stats.value) {
        return DataStateEnum.LOADED
      }

      return DataStateEnum.NO_RESULTS
    })

    const isDataLoaded = computed((): boolean => {
      return dataState.value === DataStateEnum.LOADED
    })

    const barChartComponent = new BarChartComponent()

    async function fetchBarChartData(): Promise<void> {
      if (isDateSelected.value) {
        barChartData.value = undefined
        const response = await fetchData()
        barChartData.value = response
      }
    }

    onMounted(() => {
      // TODO: Move the fetching to the composable and just work with data here
      fetchBarChartData()
    })

    let isBarChartComponentInitialized = false

    function initOrUpdateBarChartComponent(): void {
      if (isBarChartComponentInitialized) {
        barChartComponent.update(params.value)
      } else {
        barChartComponent.init(barChart.value, params.value)
        isBarChartComponentInitialized = true
      }
    }
    watch(
      () => params,
      () => {
        // We need to wait until the element is visible after the data has been loaded
        nextTick(() => {
          if (isDataLoaded.value) {
            initOrUpdateBarChartComponent()
          }
        })
      },
      { deep: true },
    )

    // TODO: Move to the useToolingReportList composable?
    watch(selectedDates, fetchBarChartData)

    return {
      barChart,
      baseClass: 'epb-bar-chart',
      loading,
      dataState,
    }
  },
})
